import { useEffect, useMemo, useState } from 'react';
import { Typography } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import type { ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { groupedAdminNav, type NavGroup, type NavItem } from './navigation';
import { useInstitution } from '../../contexts/InstitutionContext';

/**
 * Six custom nav glyphs — 16px viewBox, 1.5 stroke, no fill, currentColor.
 * Each is a single visual metaphor for the area, not a literal object:
 *   - admissions: an arched doorway (an entry)
 *   - people:     two overlapping heads (a group)
 *   - academics:  an open book spine
 *   - finance:    ascending bars on a baseline
 *   - records:    a folder tab with an inner ruling
 *   - settings:   three sliders with off-centre knobs
 * Kept as small components so they inherit the row's colour transitions
 * exactly the way Ant icons do, without the .anticon container hop.
 */
const svgProps = {
  width: 16,
  height: 16,
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  style: { display: 'block', flexShrink: 0 },
};

const IconAdmissions = () => (
  <svg {...svgProps}>
    <path d="M2.5 14V7.5a5.5 5.5 0 0 1 11 0V14" />
    <path d="M2 14h12" />
  </svg>
);

const IconPeople = () => (
  <svg {...svgProps}>
    <circle cx="6" cy="6" r="2.25" />
    <circle cx="11.25" cy="6.5" r="1.75" />
    <path d="M2 13.5c0-2.2 1.8-4 4-4s4 1.8 4 4" />
    <path d="M10 11.5c1-.7 2-1 3-1 1.5 0 2.5.9 2.5 2" />
  </svg>
);

const IconAcademics = () => (
  <svg {...svgProps}>
    <path d="M2.5 3.5c2-.5 4.5 0 5.5 1.5 1-1.5 3.5-2 5.5-1.5V13c-2-.5-4.5 0-5.5 1.5C7 13 4.5 12.5 2.5 13V3.5z" />
    <path d="M8 5v9.5" />
  </svg>
);

const IconFinance = () => (
  <svg {...svgProps}>
    <path d="M2 14h12" />
    <path d="M4.5 11.5V9" />
    <path d="M8 11.5V6" />
    <path d="M11.5 11.5V3" />
  </svg>
);

const IconRecords = () => (
  <svg {...svgProps}>
    <path d="M2 5.5a1 1 0 0 1 1-1h3l1.2 1.5H13a1 1 0 0 1 1 1V13a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V5.5z" />
    <path d="M5 10.5h6" />
  </svg>
);

const IconSettings = () => (
  <svg {...svgProps}>
    <path d="M2.5 4.5h6.5" />
    <path d="M12 4.5h1.5" />
    <circle cx="10.5" cy="4.5" r="1.5" />
    <path d="M2.5 8h1" />
    <path d="M6.5 8h7" />
    <circle cx="5" cy="8" r="1.5" />
    <path d="M2.5 11.5h7" />
    <path d="M12.5 11.5h1" />
    <circle cx="11" cy="11.5" r="1.5" />
  </svg>
);

const GROUP_ICONS: Partial<Record<NavGroup, ReactNode>> = {
  admissions: <IconAdmissions />,
  people: <IconPeople />,
  academics: <IconAcademics />,
  finance: <IconFinance />,
  records: <IconRecords />,
  settings: <IconSettings />,
};

interface Props {
  collapsed: boolean;
}

const STORAGE_KEY = 'velora.admin.sidebar.open';

function loadOpen(): NavGroup | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (raw as NavGroup) : null;
  } catch {
    return null;
  }
}

/**
 * Admin sidebar — light, minimal rail. 214px expanded / 56px collapsed.
 * Home items sit loose at the top (no group header). The remaining groups
 * behave as an accordion: opening one closes the last. The group that owns
 * the current page is always force-expanded so users never have to hunt.
 */
export function AdminSidebar({ collapsed }: Props) {
  const { institution = '' } = useParams<{ institution: string }>();
  const groups = useMemo(() => groupedAdminNav(), []);
  const location = useLocation();
  const ctx = useInstitution();

  const base = `/i/${institution}/admin`;
  const hrefFor = (path: string) => (path ? `${base}/${path}` : base);

  const selectedKey = useMemo(() => {
    const base = `/i/${institution}/admin`;
    let best: { key: string; length: number } | null = null;
    for (const g of groups) {
      for (const item of g.items) {
        const abs = item.path ? `${base}/${item.path}` : base;
        if (location.pathname === abs || location.pathname.startsWith(abs + '/')) {
          if (!best || abs.length > best.length) best = { key: item.key, length: abs.length };
        }
      }
    }
    return best?.key;
  }, [groups, institution, location.pathname]);

  const activeGroup = useMemo<NavGroup | undefined>(() => {
    for (const g of groups) {
      if (g.items.some((i) => i.key === selectedKey)) return g.group;
    }
    return undefined;
  }, [groups, selectedKey]);

  const [openGroup, setOpenGroup] = useState<NavGroup | null>(loadOpen);

  useEffect(() => {
    try {
      if (openGroup) window.localStorage.setItem(STORAGE_KEY, openGroup);
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
  }, [openGroup]);

  const toggleGroup = (g: NavGroup) => {
    setOpenGroup((prev) => (prev === g ? null : g));
  };

  const homeGroup = groups.find((g) => g.group === 'home');
  const areaGroups = groups.filter((g) => g.group !== 'home');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#1a2140' }}>
      {/* Brand */}
      <div
        style={{
          height: 54,
          display: 'flex',
          alignItems: 'center',
          padding: '0 12px',
          gap: 10,
          borderBottom: '1px solid #2a3255',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: 6,
            background: '#2a3255',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 12,
            flexShrink: 0,
          }}
        >
          {ctx.institution.name.charAt(0).toUpperCase()}
        </div>
        {!collapsed && (
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Typography.Text style={{ color: '#ffffff', fontWeight: 600, fontSize: 13, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {ctx.institution.name}
            </Typography.Text>
            <span className="mono" style={{ color: '#7d9fd0', fontSize: 10, lineHeight: 1.2, letterSpacing: 0.5, textTransform: 'uppercase' }}>
              {ctx.session.label}
              {ctx.session.term ? ` · ${ctx.session.term}` : ''}
            </span>
          </div>
        )}
      </div>

      {/* Nav */}
      <div style={{ flex: 1, overflowY: 'auto', padding: collapsed ? '8px 0 12px' : '8px 8px 12px' }}>
        {/* Home — daily items */}
        {homeGroup && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: collapsed ? 2 : 1 }}>
            {homeGroup.items.map((item) => (
              <NavRow
                key={item.key}
                item={item}
                selected={selectedKey === item.key}
                collapsed={collapsed}
                href={hrefFor(item.path)}
                showIcon={collapsed}
              />
            ))}
          </div>
        )}

        {/* Divider between daily items and areas — subtle in the rail. */}
        {homeGroup && areaGroups.length > 0 && (
          collapsed ? (
            <div style={{ height: 1, background: '#2a3255', margin: '8px 12px' }} />
          ) : (
            <>
              <div style={{ height: 1, background: '#2a3255', margin: '10px 8px 6px' }} />
              <div
                className="mono"
                style={{
                  color: '#6a7192',
                  fontSize: 9,
                  fontWeight: 600,
                  letterSpacing: '0.13em',
                  textTransform: 'uppercase',
                  padding: '4px 12px 6px',
                }}
              >
                Areas
              </div>
            </>
          )
        )}

        {/* Areas — accordion when expanded; icon-only tiles when rail. */}
        {areaGroups.map((g) => {
          const isOpen = collapsed
            ? false
            : activeGroup === g.group || openGroup === g.group;
          const isActiveArea = activeGroup === g.group;
          const totalCount = g.items.reduce((sum, i) => sum + (i.count ?? 0), 0);
          const anyHot = g.items.some((i) => i.hot && (i.count ?? 0) > 0);
          const firstChildPath = g.items[0]?.path ?? '';
          return (
            <div key={g.group} style={{ marginBottom: collapsed ? 2 : 1 }}>
              {collapsed ? (
                <Link
                  to={hrefFor(firstChildPath)}
                  title={g.label}
                  className={`pv-nav-tile ${isActiveArea ? 'active' : ''}`}
                >
                  {GROUP_ICONS[g.group]}
                  {anyHot && totalCount > 0 && <span className="pv-nav-dot" aria-hidden />}
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => toggleGroup(g.group)}
                  className={`pv-nav-row pv-nav-group-row ${isOpen ? 'open' : ''}`}
                  title={isOpen ? `Collapse ${g.label}` : `Expand ${g.label}`}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    {GROUP_ICONS[g.group]}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {g.label}
                    </span>
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {totalCount > 0 && (
                      <span className={`pv-nav-count mono ${anyHot ? 'hot' : ''}`}>{totalCount}</span>
                    )}
                    <RightOutlined
                      style={{
                        fontSize: 9,
                        color: '#6a7192',
                        transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s ease',
                      }}
                    />
                  </span>
                </button>
              )}
              {isOpen && !collapsed && (
                <div className="pv-nav-substack">
                  {g.items.map((item) => (
                    <NavRow
                      key={item.key}
                      item={item}
                      selected={selectedKey === item.key}
                      collapsed={collapsed}
                      indent
                      href={hrefFor(item.path)}
                      showIcon={false}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NavRow({
  item,
  selected,
  collapsed,
  indent,
  showIcon = true,
  href,
}: {
  item: NavItem;
  selected: boolean;
  collapsed: boolean;
  indent?: boolean;
  showIcon?: boolean;
  href: string;
}) {
  const count = item.count ?? 0;
  if (collapsed) {
    return (
      <Link
        to={href}
        title={item.label}
        className={`pv-nav-tile ${selected ? 'active' : ''}`}
      >
        {item.icon}
        {item.hot && count > 0 && <span className="pv-nav-dot" aria-hidden />}
      </Link>
    );
  }
  return (
    <Link
      to={href}
      className={`pv-nav-row ${selected ? 'selected' : ''} ${indent ? 'indent' : ''}`}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        {showIcon && item.icon}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.label}
        </span>
      </span>
      {count > 0 && (
        <span className={`pv-nav-count mono ${item.hot ? 'hot' : ''}`}>{count}</span>
      )}
    </Link>
  );
}
